// src/pages/WhyChooseUsPage.jsx
import React from "react";
import PurposePrinciplesTimeline from "./components/PurposePrinciplesTimeline";
import VerticalTabsScroller from "./components/VerticalTabsScroller";
import FAQSection from "./components/FAQSection";


const PAGE_PALETTE = {
  primary: "#2E6F40",
  mint: "#CFFFDC",
  mid: "#68BA7F",
  ink: "#253D2C",
};

const PRINCIPLES = [
    {
      title: "Client-First Communication",
      text: `We believe clarity builds trust. Every matter begins with an open discussion of facts, desired outcomes, and timelines. You’ll receive plain-English updates—not legal jargon—along with next steps and realistic expectations. Our team maintains one accountable point of contact so nothing slips through the cracks.`,
      icon: "💬",
    },
    {
      title: "Tailored Strategy, Not Templates",
      text: `No two cases are alike. We analyze the specific risks, opportunities, and jurisdictions that apply to you before designing a plan. Each roadmap is data-driven and proportionate to the matter’s complexity, ensuring efficiency without compromising quality. You’ll always know the rationale behind our approach.`,
      icon: "🧭",
    },
    {
      title: "Transparent, Predictable Fees",
      text: `We scope every engagement before beginning work—no hidden costs, no ambiguous billing. Our estimates are structured for clarity, whether fixed-fee, capped, or hourly. We keep you informed about progress versus plan so you can forecast expenses confidently and focus on decisions that matter.`,
      icon: "📄",
    },
    {
      title: "Responsive & Accessible",
      text: `In law, timing is critical. We commit to prompt responses within business hours and clear turnaround times for deliverables. Escalations are handled swiftly and directly by a partner when needed. Whether you prefer calls, emails, or virtual meetings, we stay reachable and proactive at every stage.`,
      icon: "⚡",
    },
    {
      title: "Technology-Enabled Practice",
      text: `We leverage secure platforms for document exchange, e-signatures (where permitted), and matter tracking. This digitization minimizes delays and keeps your records organized. From cloud storage to structured templates, our tech tools enhance speed, transparency, and compliance without compromising confidentiality.`,
      icon: "🗂️",
    },
    {
      title: "Cross-Border Capability",
      text: `Our lawyers coordinate seamlessly with professionals across India, the UK, and the US. We ensure alignment on jurisdictional nuances, timelines, and reporting standards. Whether it’s regulatory filing, dispute coordination, or contract execution abroad, we manage every moving part end-to-end with precision.`,
      icon: "🌍",
    },
  ];
  
export default function WhyChooseUsPage({ onNavigate }) {
  return (
    <section className="relative">
      {/* Compliance banner */}
      <div className="w-full bg-amber-50 border-b text-amber-900 text-xs md:text-sm px-4 py-2">
        This page shares information about our practice and approach only. It does not solicit work or constitute legal advice.
      </div>

      {/* Timeline section (colors controlled by PAGE_PALETTE) */}
      <PurposePrinciplesTimeline
        title="Why Clients Choose Rex Counsel"
        intro="We align law, technology, and human rights across India, the UK, and the US—turning complex, cross-border issues into clear, actionable strategy."
        principles={PRINCIPLES}
        showOrnaments={false}
        palette={PAGE_PALETTE}
      />
      {/* 🟢 ⬇️ New Section Here */}
      <VerticalTabsScroller
        palette={{
          primary: "#2E6F40",
          mint: "#CFFFDC",
          mid: "#68BA7F",
          ink: "#253D2C",
        }}
      />

<FAQSection
  title="Frequently Asked Questions"
  intro="Quick answers to common questions about our process, fees, and communication."
  faqs={[
    { q: "Do you offer an initial consultation?", a: "Yes — consultations are by appointment so we can prepare and respect your time. We confirm scope and next steps before any paid work begins." },
    { q: "How are fees quoted?", a: "We scope the matter and provide a transparent structure (fixed, capped, or hourly). You will always receive an estimate before we begin." },
    { q: "Do you work outside Delhi/NCR?", a: "Yes. We conduct online meetings and coordinate with trusted professionals across jurisdictions as required." },
    { q: "How will we share documents?", a: "We use secure file-sharing, e-signatures where permitted, and organised matter portals to keep everything on track." },
    {
        q: "What is your typical response time?",
        a: "We aim to acknowledge all client messages within the same business day and provide a substantive response or update within 24–48 hours.",
      },
      {
        q: "Do you handle urgent or time-sensitive cases?",
        a: "Yes — urgent filings and time-barred matters are prioritised with a dedicated case manager to ensure compliance and timely execution.",
      },
  ]}
  palette={{
    primary: "#2E6F40",
    mint: "#CFFFDC",
    mid: "#68BA7F",
    ink: "#253D2C",
  }}
  onConsult={() => {
    try { document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); } catch {}
  }}
/>
    </section>
  );
}
